import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { IAumicoCompanyWithMapping } from '../../models/integration/connection-mapping.model';
import { ConnectionStatus } from '../../models/integration/connection-status.enum';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { IntegrationProvider } from '../../provider/integration.provider';

/**
 * Manage modal for Abacus servers. Same three view structure as the D365 BC
 * version but with different detail content because Mandanten are not
 * enumerable through the AbaReport API.
 *
 *  1. `list`               : connected Abacus servers + "+ neuen Server" CTA
 *  2. `detail`             : Aumico Companies linked to this server with the
 *                            Mandanten ID they map to. Add / Edit / Unlink rows.
 *  3. `confirm_disconnect` : destructive disconnect confirm
 *
 * Data source on detail view:
 *  - `loadMappedCompanies(connectionId)` → `tenantDetail.aumicoCompanies`
 *    (the same vendor agnostic NgRx slice D365 BC's detail view uses;
 *    works for any vendor because the BE endpoint returns Aumico Companies
 *    by connectionId regardless of vendor).
 *
 * No `loadExternalCompanies` here: Abacus has no Mandant listing endpoint.
 * The user creates mappings explicitly via the "+ Mapping hinzufügen" button.
 */
type ManageView = 'list' | 'detail' | 'confirm_disconnect';

interface IServerRow {
    connection: IIntegrationConnection;
    needsReAuth: boolean;
}

@Component({
    selector: 'aumico-abacus-manage-modal',
    templateUrl: './abacus-manage-modal.component.html',
    styleUrls: ['./abacus-manage-modal.component.scss'],
})
export class AbacusManageModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();
    /** Emitted when the user clicks "+ Neuen Server verbinden" inside the list view. */
    @Output() public requestAddServer = new EventEmitter<void>();
    /**
     * Emitted when the user clicks "+ Mapping hinzufügen" or "Bearbeiten".
     * Parent opens the Abacus mapping modal. `aumicoCompanyId` is null for the
     * "Add" path and set for the "Edit" path so the mapping modal can pre fill.
     */
    @Output() public requestEditMapping = new EventEmitter<{
        connectionId: string;
        aumicoCompanyId: string | null;
    }>();

    /**
     * When set on open, drill directly into the detail view for this connection.
     * Used by the post Connect flow to land the user in the empty mappings list
     * of the freshly created Abacus server.
     */
    @Input() public initialConnectionId: string | null = null;

    public view: ManageView = 'list';
    public servers$!: Observable<IServerRow[]>;
    public selectedConnectionId: string | null = null;
    public selectedServer$: Observable<IServerRow | null> | null = null;
    public mappedCompanies$!: Observable<IAumicoCompanyWithMapping[]>;

    /** Held until the user confirms or cancels the destructive disconnect action. */
    public disconnectCandidate: IIntegrationConnection | null = null;

    public readonly searchTerm$ = new BehaviorSubject<string>('');
    /** Filtered + sorted view of the mapped companies for this server. */
    public filteredMappedCompanies$!: Observable<IAumicoCompanyWithMapping[]>;

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.servers$ = this.integrationProvider
            .getConnectionsByVendor$(IntegrationVendor.ABACUS)
            .pipe(
                map((connections) =>
                    connections.map((connection) => ({
                        connection,
                        needsReAuth:
                            connection.status === ConnectionStatus.EXPIRED ||
                            connection.status === ConnectionStatus.ERROR,
                    })),
                ),
            );

        this.mappedCompanies$ = this.integrationProvider.getTenantDetailCompanies$();

        this.filteredMappedCompanies$ = combineLatest([
            this.mappedCompanies$,
            this.searchTerm$,
        ]).pipe(
            map(([companies, term]) => {
                const trimmed = term.trim().toLowerCase();
                const sorted = [...companies].sort((a, b) =>
                    (a.name ?? '').localeCompare(b.name ?? ''),
                );
                if (!trimmed) return sorted;
                return sorted.filter((c) => c.name.toLowerCase().includes(trimmed));
            }),
        );

        // If the last server gets removed while the modal is open, bounce back to list.
        // Also: drill directly into the detail view on first emission if
        // `initialConnectionId` was provided (post Connect landing).
        this.servers$.pipe(takeUntil(this.destroy$)).subscribe((servers) => {
            if (this.view === 'detail' && this.selectedConnectionId != null) {
                const stillExists = servers.some(
                    (s) => s.connection.id === this.selectedConnectionId,
                );
                if (!stillExists) {
                    this.view = 'list';
                    this.selectedConnectionId = null;
                    this.selectedServer$ = null;
                }
                return;
            }
            if (
                this.initialConnectionId != null &&
                this.view === 'list' &&
                this.selectedConnectionId == null
            ) {
                const target = servers.find(
                    (s) => s.connection.id === this.initialConnectionId,
                );
                if (target) this.onSelectServer(target);
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchTerm$.complete();
    }

    // ── Navigation ───────────────────────────────────────────────────────

    public onSelectServer(row: IServerRow): void {
        this.selectedConnectionId = row.connection.id;
        this.selectedServer$ = this.servers$.pipe(
            map((rows) => rows.find((r) => r.connection.id === row.connection.id) ?? null),
        );
        this.view = 'detail';
        this.searchTerm$.next('');
        // Vendor agnostic D2 fetch: by connection id, returns Aumico Companies
        // + their mapping payload. Works for both D365 BC and Abacus.
        this.integrationProvider.loadMappedCompanies(row.connection.id);
    }

    public onBackToList(): void {
        this.view = 'list';
        this.selectedConnectionId = null;
        this.selectedServer$ = null;
        this.searchTerm$.next('');
    }

    public onAddServer(): void {
        this.requestAddServer.emit();
    }

    public onAddMapping(): void {
        if (this.selectedConnectionId == null) return;
        this.requestEditMapping.emit({
            connectionId: this.selectedConnectionId,
            aumicoCompanyId: null,
        });
    }

    public onEditMapping(row: IAumicoCompanyWithMapping): void {
        if (this.selectedConnectionId == null) return;
        this.requestEditMapping.emit({
            connectionId: this.selectedConnectionId,
            aumicoCompanyId: row.id,
        });
    }

    public onUnlink(row: IAumicoCompanyWithMapping): void {
        // Direct DELETE, same pattern as D365 BC manage modal. No confirm
        // because the action is recoverable (re-map via row Edit).
        this.integrationProvider.deleteMapping(row.id, IntegrationVendor.ABACUS);
    }

    public onSearchTermChange(term: string): void {
        this.searchTerm$.next(term);
    }

    // ── Disconnect (destructive) ─────────────────────────────────────────

    public onRequestDisconnect(connection: IIntegrationConnection): void {
        this.disconnectCandidate = connection;
        this.view = 'confirm_disconnect';
    }

    public onCancelDisconnect(): void {
        this.disconnectCandidate = null;
        this.view = this.selectedConnectionId != null ? 'detail' : 'list';
    }

    public onConfirmDisconnect(): void {
        if (this.disconnectCandidate == null) return;
        const id = this.disconnectCandidate.id;
        this.integrationProvider.disconnect(id);
        this.disconnectCandidate = null;
        this.view = 'list';
        if (this.selectedConnectionId === id) {
            this.selectedConnectionId = null;
            this.selectedServer$ = null;
        }
    }

    public onClose(): void {
        this.closed.emit();
    }

    // ── Helpers exposed to template ──────────────────────────────────────

    public trackByConnectionId(_: number, row: IServerRow): string {
        return row.connection.id;
    }

    public trackByAumicoCompanyId(_: number, row: IAumicoCompanyWithMapping): string {
        return row.id;
    }
}
